// ✅ src/components/screens/AdminSupportInboxScreen.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../../lib/firebase";
import {
  addDoc,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

function timeAgo(ts) {
  try {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "now";
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `${days}d`;
  } catch {
    return "";
  }
}

function fmtTime(ts) {
  try {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function toMillis(ts) {
  try {
    if (!ts) return 0;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.getTime();
  } catch {
    return 0;
  }
}

function useIsMobile(breakpoint = 900) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function AdminSupportInboxScreen({ goBack, role }) {
  // ✅ FIX: normalize role
  const roleNorm = safe(role).toUpperCase();
  const isAllowed = roleNorm === "ADMIN" || roleNorm === "WORKER";

  const isMobile = useIsMobile(900);

  const [tab, setTab] = useState("OPEN"); // OPEN | PENDING | SOLVED | ALL
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selected, setSelected] = useState(null);

  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [msg, setMsg] = useState("");

  const bottomRef = useRef(null);

  // ✅ Tickets list (NO orderBy in query => avoids composite index)
  useEffect(() => {
    if (!isAllowed) return;

    setLoading(true);
    setMsg("");

    const ref = collection(db, "supportTickets");
    const q =
      tab === "ALL"
        ? query(ref, limit(200))
        : query(ref, where("status", "==", tab), limit(200));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        rows.sort((a, b) => {
          const aT = toMillis(a.lastMessageAt) || toMillis(a.createdAt);
          const bT = toMillis(b.lastMessageAt) || toMillis(b.createdAt);
          return bT - aT;
        });

        setTickets(rows);
        setLoading(false);

        if (selected?.id) {
          const stillHere = rows.some((r) => r.id === selected.id);
          if (!stillHere) setSelected(null);
        }
      },
      (e) => {
        console.error(e);
        setTickets([]);
        setSelected(null);
        setLoading(false);
        setMsg(`❌ Tickets failed: ${e?.message || "error"}`);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isAllowed]);

  // ✅ Messages thread
  useEffect(() => {
    if (!selected?.id) {
      setMessages([]);
      return;
    }

    const mq = query(
      collection(db, "supportTickets", selected.id, "messages"),
      orderBy("createdAt", "asc"),
      limit(400)
    );

    const unsub = onSnapshot(
      mq,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMessages(rows);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
      },
      (e) => {
        console.error(e);
        setMessages([]);
        setMsg(`❌ Messages failed: ${e?.message || "error"}`);
      }
    );

    return () => unsub();
  }, [selected?.id]);

  const canReply = useMemo(
    () => safe(reply).length >= 1 && !!selected?.id,
    [reply, selected]
  );

  async function act(id, patch) {
    setMsg("");
    try {
      await updateDoc(doc(db, "supportTickets", id), {
        ...patch,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error(e);
      setMsg(`❌ Failed: ${e?.message || "rules/index?"}`);
    }
  }

  async function assignToMe() {
    if (!selected) return;
    const staffUid = auth.currentUser?.uid || "staff";
    const curStatus = safe(selected.status).toUpperCase() || "OPEN";

    await act(selected.id, {
      assignedTo: staffUid,
      status: curStatus === "SOLVED" ? "SOLVED" : "PENDING",
    });
    setMsg("✅ Assigned");
  }

  async function markSolved() {
    if (!selected) return;
    await act(selected.id, { status: "SOLVED" });
    setMsg("✅ Marked solved");
  }

  async function reopen() {
    if (!selected) return;
    await act(selected.id, { status: "OPEN" });
    setMsg("✅ Reopened");
  }

  async function sendReply() {
    if (!selected || !canReply) return;

    const text = safe(reply);
    const staffUid = auth.currentUser?.uid || "staff";

    try {
      setReply("");

      await addDoc(collection(db, "supportTickets", selected.id, "messages"), {
        from: "STAFF",
        staffUid,
        text,
        createdAt: serverTimestamp(),
      });

      await act(selected.id, {
        lastMessageText: text,
        lastMessageAt: serverTimestamp(),
        status: "PENDING",
      });

      setMsg("✅ Reply sent");
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
    } catch (e) {
      console.error(e);
      setMsg(`❌ Reply failed: ${e?.message || "rules?"}`);
      setReply(text);
    }
  }

  if (!isAllowed) {
    return (
      <div style={S.page}>
        <div style={S.header}>
          <button style={S.backBtn} onClick={goBack} type="button">
            ←
          </button>
          <div>
            <div style={S.title}>Support Inbox</div>
            <div style={S.subtitle}>Access denied</div>
          </div>
        </div>
        <div style={S.glassCard}>
          You need <b>ADMIN</b> or <b>WORKER</b> role.
          <div style={{ marginTop: 8, fontWeight: 900, fontSize: 12, color: "rgba(15,23,42,0.65)" }}>
            Your role: <b>{roleNorm || "—"}</b>
          </div>
        </div>
      </div>
    );
  }

  // On mobile: if ticket selected, show only chat view
  const showChatOnlyMobile = isMobile && !!selected;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <button style={S.backBtn} onClick={goBack} type="button">
          ←
        </button>
        <div style={{ minWidth: 0 }}>
          <div style={S.title}>Support Inbox</div>
          <div style={S.subtitle}>Real-time ticket chat</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          <span style={S.roleBadge}>{roleNorm}</span>
        </div>
      </div>

      <div style={S.container}>
        <div style={S.topBar}>
          <div style={S.tabs}>
            {["OPEN", "PENDING", "SOLVED", "ALL"].map((t) => (
              <button
                key={t}
                style={{ ...S.tab, ...(tab === t ? S.tabOn : null) }}
                onClick={() => setTab(t)}
                type="button"
              >
                {t}
              </button>
            ))}
          </div>

          {msg ? <div style={S.toast}>{msg}</div> : <div />}
        </div>

        <div style={{ ...S.split, ...(showChatOnlyMobile ? S.splitMobileChat : null) }}>
          {/* LEFT: Ticket list */}
          <div style={{ ...S.left, ...(showChatOnlyMobile ? S.hideOnMobile : null) }}>
            <div style={S.leftHeader}>
              <div style={S.leftTitle}>Tickets</div>
              <div style={S.leftHint}>
                {loading ? "Loading…" : `${tickets.length} ticket(s)`}
              </div>
            </div>

            <div style={S.ticketList}>
              {loading ? (
                <div style={S.skelCard}>Loading tickets…</div>
              ) : tickets.length === 0 ? (
                <div style={S.skelCard}>No tickets here.</div>
              ) : (
                tickets.map((t) => {
                  const isOn = selected?.id === t.id;
                  const lastAt = t.lastMessageAt || t.createdAt;
                  const st = safe(t.status).toUpperCase() || "OPEN";

                  return (
                    <button
                      key={t.id}
                      style={{ ...S.ticket, ...(isOn ? S.ticketOn : null) }}
                      onClick={() => setSelected(t)}
                      type="button"
                    >
                      <div style={S.ticketTop}>
                        <div style={S.ticketSubject}>{t.subject || "Support ticket"}</div>
                        <span style={{ ...S.statusPill, ...(S.pills[st] || S.pills.DEFAULT) }}>
                          {st}
                        </span>
                      </div>

                      <div style={S.ticketMid}>
                        <div style={S.ticketPreview}>{t.lastMessageText || "No messages yet"}</div>
                      </div>

                      <div style={S.ticketBottom}>
                        <div style={S.ticketUser}>{(t.name || t.email || "User").slice(0, 28)}</div>
                        <div style={S.ticketTime}>{timeAgo(lastAt)}</div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* RIGHT: Chat panel */}
          <div style={S.right}>
            {!selected ? (
              <div style={S.emptyChat}>
                <div style={S.emptyIcon}>💬</div>
                <div style={S.emptyTitle}>Select a ticket</div>
                <div style={S.emptySub}>Pick a ticket on the left to view the chat.</div>
              </div>
            ) : (
              <>
                <div style={S.chatHeader}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                    {isMobile ? (
                      <button
                        style={S.mobileBack}
                        onClick={() => setSelected(null)}
                        title="Back to tickets"
                        type="button"
                      >
                        ←
                      </button>
                    ) : null}

                    <div style={{ minWidth: 0 }}>
                      <div style={S.chatTitle}>{selected.subject || "Ticket"}</div>
                      <div style={S.chatMeta}>
                        <span style={S.mono}>#{selected.id.slice(0, 8)}</span>
                        <span style={S.dot}>•</span>
                        <span>{selected.email || selected.uid || "user"}</span>
                        <span style={S.dot}>•</span>
                        <span style={S.mono}>{selected.assignedTo ? "assigned" : "unassigned"}</span>
                      </div>
                    </div>
                  </div>

                  <div style={S.chatActions}>
                    <button style={S.actionBtn} onClick={assignToMe} type="button">
                      Assign
                    </button>
                    {safe(selected.status).toUpperCase() === "SOLVED" ? (
                      <button style={{ ...S.actionBtn, ...S.warnBtn }} onClick={reopen} type="button">
                        Reopen
                      </button>
                    ) : (
                      <button style={{ ...S.actionBtn, ...S.goodBtn }} onClick={markSolved} type="button">
                        Solved
                      </button>
                    )}
                  </div>
                </div>

                <div style={S.thread}>
                  {messages.length === 0 ? (
                    <div style={S.threadEmpty}>No messages yet.</div>
                  ) : (
                    messages.map((m) => {
                      const staff = m.from === "STAFF";
                      const system = m.from === "SYSTEM";
                      const side = system ? "center" : staff ? "right" : "left";

                      return (
                        <div
                          key={m.id}
                          style={{
                            ...S.msgRow,
                            justifyContent:
                              side === "center"
                                ? "center"
                                : side === "right"
                                ? "flex-end"
                                : "flex-start",
                          }}
                        >
                          <div
                            style={{
                              ...S.bubble,
                              ...(side === "right" ? S.bubbleMe : null),
                              ...(side === "left" ? S.bubbleUser : null),
                              ...(side === "center" ? S.bubbleSystem : null),
                            }}
                          >
                            <div style={S.bText}>{m.text}</div>
                            <div style={S.bMeta}>
                              {system ? "system" : staff ? "staff" : "user"} · {fmtTime(m.createdAt)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={bottomRef} />
                </div>

                <div style={S.composer}>
                  <input
                    style={S.input}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type a reply…"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") sendReply();
                    }}
                  />
                  <button
                    style={{ ...S.send, opacity: canReply ? 1 : 0.55 }}
                    disabled={!canReply}
                    onClick={sendReply}
                    type="button"
                  >
                    Send
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(900px 500px at 20% 10%, rgba(99,102,241,0.22), transparent 60%), radial-gradient(900px 500px at 80% 20%, rgba(236,72,153,0.18), transparent 60%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 55%, #f8fafc 100%)",
    padding: 14,
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
    color: "#0f172a",
  },

  header: {
    maxWidth: 1200,
    margin: "0 auto 12px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  backBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    cursor: "pointer",
    fontWeight: 900,
  },
  title: { fontWeight: 950, fontSize: 20, letterSpacing: -0.3 },
  subtitle: { marginTop: 2, fontWeight: 800, fontSize: 12, color: "rgba(15,23,42,0.55)" },
  roleBadge: {
    padding: "7px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.80)",
    fontSize: 12,
    fontWeight: 950,
  },

  container: {
    maxWidth: 1200,
    margin: "0 auto",
  },

  topBar: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 12,
    alignItems: "center",
    marginBottom: 12,
  },

  tabs: { display: "flex", gap: 10, flexWrap: "wrap" },
  tab: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.75)",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 12,
  },
  tabOn: {
    background: "rgba(99,102,241,0.14)",
    border: "1px solid rgba(99,102,241,0.26)",
  },

  toast: {
    justifySelf: "end",
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "rgba(255,255,255,0.70)",
    fontWeight: 900,
    fontSize: 12,
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
  },

  split: {
    display: "grid",
    gridTemplateColumns: "410px 1fr",
    gap: 12,
    minHeight: "72vh",
  },
  splitMobileChat: {
    gridTemplateColumns: "1fr",
  },

  glassCard: {
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.82)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
  },

  left: {
    borderRadius: 20,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.72)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  hideOnMobile: { display: "none" },

  leftHeader: {
    padding: 14,
    borderBottom: "1px solid rgba(15,23,42,0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
  },
  leftTitle: { fontWeight: 950, fontSize: 14 },
  leftHint: { fontWeight: 850, fontSize: 12, color: "rgba(15,23,42,0.55)" },

  ticketList: {
    padding: 12,
    overflowY: "auto",
    display: "grid",
    gap: 10,
    minHeight: 0,
  },

  skelCard: {
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    fontWeight: 900,
    color: "rgba(15,23,42,0.65)",
  },

  ticket: {
    textAlign: "left",
    padding: 12,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.88)",
    cursor: "pointer",
    transition: "transform 0.12s ease",
  },
  ticketOn: {
    border: "1px solid rgba(99,102,241,0.28)",
    background: "rgba(99,102,241,0.10)",
  },

  ticketTop: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    justifyContent: "space-between",
  },
  ticketSubject: {
    fontWeight: 950,
    fontSize: 13,
    color: "#0f172a",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 260,
  },

  statusPill: {
    padding: "6px 9px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 950,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.9)",
  },
  pills: {
    OPEN: { background: "rgba(236,72,153,0.10)", border: "1px solid rgba(236,72,153,0.20)" },
    PENDING: { background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.22)" },
    SOLVED: { background: "rgba(34,197,94,0.10)", border: "1px solid rgba(34,197,94,0.20)" },
    DEFAULT: { background: "rgba(2,6,23,0.04)", border: "1px solid rgba(2,6,23,0.10)" },
  },

  ticketMid: { marginTop: 8 },
  ticketPreview: {
    fontSize: 12,
    fontWeight: 850,
    color: "rgba(15,23,42,0.65)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  ticketBottom: {
    marginTop: 10,
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
  },
  ticketUser: {
    fontSize: 11,
    fontWeight: 900,
    color: "rgba(15,23,42,0.55)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 240,
  },
  ticketTime: { fontSize: 11, fontWeight: 950, color: "rgba(15,23,42,0.55)" },

  right: {
    borderRadius: 20,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.80)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },

  emptyChat: {
    height: "100%",
    padding: 18,
    display: "grid",
    placeItems: "center",
    textAlign: "center",
  },
  emptyIcon: { fontSize: 34 },
  emptyTitle: { fontWeight: 950, fontSize: 16, marginTop: 6 },
  emptySub: { fontWeight: 850, fontSize: 12, color: "rgba(15,23,42,0.55)", marginTop: 4 },

  chatHeader: {
    padding: 14,
    borderBottom: "1px solid rgba(15,23,42,0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  chatTitle: {
    fontWeight: 950,
    fontSize: 14,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: 520,
  },
  chatMeta: { marginTop: 4, fontWeight: 850, fontSize: 12, color: "rgba(15,23,42,0.55)" },
  dot: { margin: "0 6px", opacity: 0.6 },
  mono: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 900 },

  chatActions: { display: "flex", gap: 10, flexWrap: "wrap" },
  actionBtn: {
    padding: "9px 10px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontWeight: 950,
    fontSize: 12,
  },
  goodBtn: {
    background: "rgba(34,197,94,0.12)",
    border: "1px solid rgba(34,197,94,0.22)",
  },
  warnBtn: {
    background: "rgba(245,158,11,0.12)",
    border: "1px solid rgba(245,158,11,0.22)",
  },

  mobileBack: {
    width: 40,
    height: 40,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    cursor: "pointer",
    fontWeight: 950,
  },

  thread: {
    flex: 1,
    padding: 14,
    overflowY: "auto",
    background:
      "linear-gradient(180deg, rgba(99,102,241,0.05), transparent 25%), radial-gradient(900px 450px at 20% 10%, rgba(236,72,153,0.08), transparent 60%)",
    minHeight: 0,
  },
  threadEmpty: {
    padding: 14,
    borderRadius: 16,
    border: "1px dashed rgba(15,23,42,0.18)",
    background: "rgba(255,255,255,0.6)",
    fontWeight: 900,
    color: "rgba(15,23,42,0.65)",
  },

  msgRow: { display: "flex", marginBottom: 10 },
  bubble: {
    maxWidth: "78%",
    borderRadius: 16,
    padding: "10px 12px",
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.95)",
    boxShadow: "0 10px 25px rgba(15, 23, 42, 0.05)",
  },
  bubbleMe: {
    background: "linear-gradient(135deg, rgba(99,102,241,0.18), rgba(236,72,153,0.12))",
    border: "1px solid rgba(99,102,241,0.25)",
  },
  bubbleUser: {
    background: "rgba(255,255,255,0.92)",
  },
  bubbleSystem: {
    background: "rgba(2,6,23,0.05)",
    border: "1px solid rgba(2,6,23,0.10)",
  },
  bText: { fontWeight: 850, lineHeight: 1.35 },
  bMeta: { marginTop: 6, fontSize: 11, fontWeight: 900, color: "rgba(15,23,42,0.55)" },

  composer: {
    padding: 12,
    borderTop: "1px solid rgba(15,23,42,0.08)",
    display: "flex",
    gap: 10,
    alignItems: "center",
    background: "rgba(255,255,255,0.75)",
  },
  input: {
    flex: 1,
    padding: "12px 12px",
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 900,
    background: "rgba(255,255,255,0.95)",
  },
  send: {
    padding: "12px 14px",
    borderRadius: 16,
    border: "none",
    background: "linear-gradient(135deg, #6366f1 0%, #ec4899 100%)",
    color: "white",
    fontWeight: 950,
    cursor: "pointer",
    minWidth: 92,
  },
};
